import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import reduce from "lodash/reduce";
import filter from "lodash/filter";
import get from "lodash/get";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import toLower from "lodash/toLower";

import styled from "styled-components";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Select from "@mui/material/Select";

import Button from "../shared/Button";
import {
  CATEGORIES,
  ESTATE_DISPLAY_STATUS,
  ESTATE_STATUS,
  PURPOSES,
  PURPOSE_STATUS,
  SUBCATEGORIES,
} from "../../helpers/constants";
import useEstatesFilterStore from "../stores/useEstatesFilterStore";
import useRepresentatives from "../hooks/useRepresentative";
import useOffices from "../hooks/useOffices";
import AutocompleteController from "../forms/AutocompleteController";
import SelectController from "../forms/SelectController";
import TextFieldController from "../forms/TextFieldController";
import CheckboxController from "../forms/CheckboxController";

const EstateFilter = ({ closeModal }) => {
  const state = useEstatesFilterStore((state) => state);
  const {
    purpose,
    purposeStatus,
    categories,
    subCategories,
    agent,
    representatives,
    offices,
    displayStatus,
    status,
    priceMin,
    priceMax,
    submitFilter,
    resetFilter,
    isTopParent,
  } = state;

  const { control, handleSubmit, setValue, watch, getValues } = useForm({
    defaultValues: {
      purpose,
      purposeStatus,
      categories,
      subCategories,
      agent,
      representatives,
      offices,
      displayStatus,
      status,
      priceMin,
      priceMax,
      isTopParent,
    },
  });

  useEffect(() => {
    setValue("purpose", purpose);
    setValue("purposeStatus", purposeStatus);
    setValue("categories", categories);
    setValue("subCategories", subCategories);
    setValue("agent", agent);
    setValue("representatives", representatives);
    setValue("offices", offices);
    setValue("displayStatus", displayStatus);
    setValue("status", status);
    setValue("priceMin", priceMin);
    setValue("priceMax", priceMax);
    setValue("isTopParent", isTopParent);
  }, [state]);

  const { data: representativesData } = useRepresentatives();
  const { data: officesData } = useOffices();

  const { representativesOptions, representativesById } = useMemo(() => {
    const representativesOptions = sortBy(representativesData, (item) =>
      toLower(get(item, "firstName"))
    );
    const representativesById = reduce(
      representativesOptions,
      (acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = curr;
        }
        return acc;
      },
      {}
    );

    return {
      representativesOptions,
      representativesById,
    };
  }, [representativesData]);

  const { officesOptions, officesById } = useMemo(() => {
    const officesOptions = sortBy(officesData, (item) =>
      toLower(get(item, "name"))
    );
    const officesById = reduce(
      officesOptions,
      (acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = curr;
        }
        return acc;
      },
      {}
    );
    return {
      officesOptions,
      officesById,
    };
  }, [officesData]);

  const { purposeStatusOption, purposeStatusById } = useMemo(() => {
    const purpose = getValues("purpose");
    const purposeStatusOption = filter(
      PURPOSE_STATUS,
      (ps) => ps.purposeId === purpose
    );
    const purposeStatusById = reduce(
      purposeStatusOption,
      (acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = curr;
        }
        return acc;
      },
      {}
    );
    return {
      purposeStatusOption,
      purposeStatusById,
    };
  }, [watch("purpose")]);

  const estateDisplaysStatusById = useMemo(
    () =>
      reduce(
        ESTATE_DISPLAY_STATUS,
        (acc, curr) => {
          if (!acc[curr.id]) {
            acc[curr.id] = curr;
          }
          return acc;
        },
        {}
      ),
    []
  );

  const categoriesById = useMemo(
    () =>
      reduce(
        CATEGORIES,
        (acc, curr) => {
          if (!acc[curr.id]) {
            acc[curr.id] = curr;
          }
          return acc;
        },
        {}
      ),
    []
  );

  const { subCategories: subCatOptions, subCategoriesById } = useMemo(() => {
    const categories = getValues("categories");

    const subCategories = isEmpty(categories)
      ? SUBCATEGORIES
      : filter(SUBCATEGORIES, (sc) => includes(categories, sc.categoryId));

    const subCategoriesById = reduce(
      subCategories,
      (acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = curr;
        }
        return acc;
      },
      {}
    );

    return {
      subCategories,
      subCategoriesById,
    };
  }, [watch("categories")]);

  const onSubmit = (data) => {
    submitFilter(data);
    !!closeModal && closeModal();
  };

  return (
    <FilterContainer className="filter-container">
      <form onSubmit={handleSubmit(onSubmit)}>
        <p>Filtre</p>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Type de transaction</label>
              <SelectController
                control={control}
                name="purpose"
                options={PURPOSES}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Status de transaction</label>
              <AutocompleteController
                control={control}
                name="purposeStatus"
                options={purposeStatusOption}
                optionsById={purposeStatusById}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Catégorie de bien</label>
              <AutocompleteController
                control={control}
                name="categories"
                options={CATEGORIES}
                optionsById={categoriesById}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Type de bien</label>
              <AutocompleteController
                control={control}
                name="subCategories"
                options={subCatOptions}
                optionsById={subCategoriesById}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Représentants</label>
              <AutocompleteController
                control={control}
                name="representatives"
                options={representativesOptions}
                optionsById={representativesById}
                getOptionLabel={(option, optionsById) => {
                  const representative = optionsById[option];
                  return `${representative.firstName} ${representative.name}`;
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Bureau</label>
              <AutocompleteController
                control={control}
                name="offices"
                options={officesOptions}
                optionsById={officesById}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Visibilité</label>
              <AutocompleteController
                control={control}
                name="displayStatus"
                options={ESTATE_DISPLAY_STATUS}
                optionsById={estateDisplaysStatusById}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Status du bien</label>
              <SelectController
                control={control}
                name="status"
                options={ESTATE_STATUS}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Prix</label>
              <TextFieldController
                name="priceMin"
                control={control}
                placeholder="Min"
                type="number"
              />
            </Grid>
            <Grid className="d-flex align-items-end" size={{ xs: 12, md: 6 }}>
              <TextFieldController
                name="priceMax"
                control={control}
                placeholder="Max"
                type="number"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Type de document</label>
              <Select fullWidth></Select>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="form-label">Status des documents</label>
              <Select fullWidth></Select>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CheckboxController
                name="isTopParent"
                control={control}
                label="Afficher uniquement les projets"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <Button
                type="submit"
                className="w-full"
                color="primary"
                variant="contained"
              >
                Appliquer
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                type="button"
                className="w-full"
                color="danger"
                variant="outlined"
                noBorder
                onClick={resetFilter}
              >
                Réinitialiser le filtre
              </Button>
            </Grid>
          </Grid>
        </Box>
      </form>
    </FilterContainer>
  );
};

const FilterContainer = styled.div`
  max-width: 773px;

  label {
    display: block;
  }

  &.filter-container {
    padding: 0 30px 30px 30px;

    & .row > div:first-child {
      padding: 0;
    }

    & .row > div:last-child {
      padding-right: 0;
    }

    & .row {
      margin-top: 10px;
    }
  }

  & > form > p {
    font-size: 36px;
    font-weight: 700;
    color: var(--text-color);
  }

  label.form-label {
    font-weight: 500;
    color: var(--text-color);
  }

  .filter-input {
    width: 282px;
  }

  .filter-action-container {
    gap: 10px;
  }

  .w-full {
    width: 100%;
  }
`;

export default EstateFilter;
